/**
 * One cell of the form. Computed values render petrol (`auto`) and stay fully
 * editable — typing into one freezes it as a manual override, clearing it hands
 * the field back to the calculator.
 *
 * Typing is never touched: a value is tidied (normalize) and checked (validate)
 * on blur only, so the caret never jumps and half-typed input is never "fixed".
 */
import {
  createContext, useContext, useState, type CSSProperties, type KeyboardEvent, type ReactNode,
} from 'react';
import { dateToIso, inputModeFor, isoToDate, kindFor, label, normalize, validate } from '../fields';
import { useT, warnText, type T } from '../i18n';
import { useField, usePlanStore } from '../planContext';
import type { RouteMark, WarnRef } from '../store';

const ReadOnlyContext = createContext(false);
export const ReadOnlyProvider = ReadOnlyContext.Provider;
export const useReadOnly = (): boolean => useContext(ReadOnlyContext);

/* ---------------- keyboard flow ---------------- */

const FOCUSABLE = 'input:not([type=hidden]), select, textarea';

const isReachable = (el: HTMLElement): boolean => {
  if (el.tabIndex < 0) return false;
  if ((el as HTMLInputElement).disabled) return false;
  if ((el as HTMLInputElement).readOnly) return false;
  // display:none (collapsed section, the route table on phones, the leg cards on desktop)
  return el.offsetParent !== null || el.getClientRects().length > 0;
};

/** Enter behaves like Tab: the next reachable field in DOM order, then select its text. */
function focusNextField(from: HTMLElement): void {
  const root: ParentNode = from.closest('main') ?? document;
  const all = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isReachable);
  const i = all.indexOf(from);
  // the field itself is not reachable (a read-only shared plan): stay put, do not jump to the top
  if (i < 0) {
    from.blur();
    return;
  }
  const next = all[i + 1];
  if (!next) {
    from.blur();
    return;
  }
  next.focus();
  if (next instanceof HTMLInputElement && next.type !== 'checkbox') next.select();
}

/** Enter = next field, Escape = leave the field (mobile: closes the keyboard). */
export function onFieldKeyDown(e: KeyboardEvent<HTMLElement>): void {
  if (e.key === 'Enter') {
    e.preventDefault();
    focusNextField(e.currentTarget);
  } else if (e.key === 'Escape') {
    e.currentTarget.blur();
  }
}

export interface FieldInputProps {
  id: string;
  /** fixed pixel width, as in the predecessor's Formular view */
  width?: number;
  placeholder?: string;
  list?: string;
  ariaLabel?: string;
  className?: string;
}

/** Tooltip text: the field's warning if it has one, otherwise its label. */
const fieldTitle = (t: T, id: string, warn: WarnRef | null, ariaLabel?: string): string =>
  warn ? warnText(t, warn) : (ariaLabel ?? label(id, t));

/**
 * The map-derived suffix on a field's tooltip. `'s'` says where the number came
 * from, `'o'` says the pilot's number and the map's no longer agree — neither is
 * an error, so neither uses the warning colours.
 */
const routeNote = (t: T, mark: RouteMark): string =>
  mark === 's' ? ' – ' + t('map.field.synced') : mark === 'o' ? ' – ' + t('map.field.differs') : '';

/**
 * The suffix for the five sources that cannot "differ": the aircraft profile,
 * the METAR/TAF briefing, the openAIP feed, the winds aloft and the terrain.
 * Editing such a cell takes it away from its source altogether, so a marked cell
 * is always exactly what that source says.
 *
 * `'aircraft-est'` says the number is the profile's OTHER tier — a type-typical
 * figure or one nobody has ticked off against this airframe's POH / weighing
 * report. `mb_leer_m` from a preset is the case that matters: it is the base of
 * the P28–P32 sum, and it must not read as a weighing-report number.
 */
const derivedNote = (t: T, f: { derived: string; derivedFrom: string }): string => {
  // The terrain is the one source that can name itself with no label: it does two
  // different jobs and the LABEL is what tells them apart (`PlanStore.terrainLabel`) —
  // a SafeAlt read off a profile carries the corridor it was read over, an aerodrome
  // elevation is a single DEM point and carries nothing. So it is answered before the
  // guard below, which exists for a source whose name is simply not loaded yet.
  if (f.derived === 'terrain') {
    return ' – ' + (f.derivedFrom
      ? t('terrain.field.derived', { id: f.derivedFrom })
      : t('terrain.field.elev'));
  }
  // The wind is the terrain's other half: the forecast HOUR sharpens the note when
  // it is known ("aus dem Wind, Vorhersage 12Z") and is simply absent on a plan
  // whose meta predates it — but the cell was still filled by the forecast and has
  // to say so either way, so this is answered before the guard below.
  if (f.derived === 'winds') {
    return ' – ' + (f.derivedFrom
      ? t('winds.field.derived', { id: f.derivedFrom })
      : t('winds.field.derived.plain'));
  }
  // no registration means the profile is not loaded (a shared link, or an offline
  // plan opened before the aircraft list arrives): mark the cell, name no source
  if (!f.derivedFrom) return '';
  if (f.derived === 'aircraft') return ' – ' + t('ac.field.derived', { reg: f.derivedFrom });
  if (f.derived === 'aircraft-est') return ' – ' + t('ac.field.derived.est', { reg: f.derivedFrom });
  if (f.derived === 'wx') return ' – ' + t('wx.field.derived', { id: f.derivedFrom });
  if (f.derived === 'geo') return ' – ' + t('geo.field.derived', { id: f.derivedFrom });
  return '';
};

/**
 * The derived-cell class: the same dashed underline whatever wrote the value —
 * except that an unconfirmed profile number gets its own (dotted) one, so the two
 * tiers are as far apart in the form as they are on the aircraft page.
 */
const derivedClass = (f: { route: RouteMark; derived: string }): string =>
  f.route === 's' ? 'rsync'
    : f.route === 'o' ? 'rdiff'
      : f.derived === 'aircraft' || f.derived === 'wx' || f.derived === 'geo' ||
        f.derived === 'winds' || f.derived === 'terrain'
        ? 'rsync'
        : f.derived === 'aircraft-est' ? 'rsync acest' : '';

export function FieldInput({ id, width, placeholder, list, ariaLabel, className }: FieldInputProps) {
  const store = usePlanStore();
  const readOnly = useReadOnly();
  const t = useT();
  const f = useField(id);
  const [focused, setFocused] = useState(false);
  const kind = kindFor(id);
  // while the field has focus the value is still half-typed — do not judge it yet
  const bad = focused ? null : validate(kind, f.value);
  const cls = [className, f.auto ? 'auto' : '', f.flag, derivedClass(f), bad ? 'invalid' : '']
    .filter(Boolean)
    .join(' ');
  const style: CSSProperties | undefined = width ? { width: width + 'px' } : undefined;
  const mode = inputModeFor(id);
  const name = ariaLabel ?? label(id, t);
  const base = fieldTitle(t, id, f.warn, ariaLabel) + routeNote(t, f.route) + derivedNote(t, f);
  const title = bad ? base + ' – ' + t(bad) : base;
  const upper = kind === 'icao' || kind === 'code';
  return (
    <input
      type="text"
      value={f.value}
      className={cls || undefined}
      style={style}
      title={title}
      aria-label={name}
      aria-invalid={bad ? true : undefined}
      autoComplete="off"
      spellCheck={false}
      readOnly={readOnly}
      {...(mode ? { inputMode: mode } : {})}
      {...(upper ? { autoCapitalize: 'characters' as const, autoCorrect: 'off' } : {})}
      {...(kind === 'code' ? { maxLength: 1 } : {})}
      {...(list ? { list } : {})}
      {...(placeholder ? { placeholder } : {})}
      onChange={e => store.set(id, e.target.value)}
      onKeyDown={onFieldKeyDown}
      onFocus={e => {
        setFocused(true);
        store.setActive(id);
        // overriding a computed value should cost one keystroke, not a select-all
        if (f.auto) e.target.select();
      }}
      onBlur={e => {
        setFocused(false);
        store.setActive(null);
        if (readOnly) return;
        const v = normalize(kind, e.target.value);
        if (v !== f.value) store.set(id, v);
        store.recompute();
      }}
    />
  );
}

/** Labelled field: the `.ff` stack of caption + input. */
export function FF({
  id,
  text,
  width,
  placeholder,
  list,
  span,
  ariaLabel,
}: FieldInputProps & { text: string; span?: 2 | 4 }) {
  const cls = 'ff' + (span === 2 ? ' sp2' : span === 4 ? ' sp4' : '');
  return (
    <label className={cls}>
      <span>{text}</span>
      <FieldInput
        id={id}
        {...(width ? { width } : {})}
        {...(placeholder ? { placeholder } : {})}
        {...(list ? { list } : {})}
        {...(ariaLabel ? { ariaLabel } : {})}
      />
    </label>
  );
}

/** An option of a SelectField: the stored string plus what the pilot reads. */
export interface FieldOption {
  value: string;
  text?: string;
}

/**
 * A labelled <select> over a stored string. A stored value that is not in the
 * list is kept as an extra option, so a plan written by an older version (or
 * imported from elsewhere) stays editable instead of losing the value.
 */
export function SelectField({
  id,
  text,
  options,
  width,
  emptyText = '—',
}: {
  id: string;
  text: string;
  options: readonly FieldOption[];
  width?: number;
  emptyText?: string;
}) {
  const store = usePlanStore();
  const readOnly = useReadOnly();
  const t = useT();
  const f = useField(id);
  const cur = f.value.trim();
  const extra = cur !== '' && !options.some(o => o.value === cur);
  const cls = [f.auto ? 'auto' : '', f.flag, derivedClass(f)].filter(Boolean).join(' ');
  const name = label(id, t);
  return (
    <label className="ff">
      <span>{text}</span>
      <select
        value={cur}
        className={cls || undefined}
        style={width ? { width: width + 'px' } : undefined}
        title={fieldTitle(t, id, f.warn) + derivedNote(t, f)}
        aria-label={name}
        disabled={readOnly}
        onKeyDown={onFieldKeyDown}
        onChange={e => {
          store.set(id, e.target.value);
          store.recompute();
        }}
      >
        <option value="">{emptyText}</option>
        {extra ? <option value={cur}>{cur}</option> : null}
        {options.map(o => (
          <option key={o.value} value={o.value}>
            {o.text ?? o.value}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The Datum field: a native date picker over the stored "d.m.yyyy" string.
 * A stored value the parser does not understand is shown in a plain text input
 * instead — nothing a pilot typed is ever silently dropped.
 */
export function DateField({ id, text, width }: { id: string; text: string; width?: number }) {
  const store = usePlanStore();
  const readOnly = useReadOnly();
  const t = useT();
  const f = useField(id);
  const iso = dateToIso(f.value);
  const name = label(id, t);
  if (f.value.trim() !== '' && iso === '') {
    return <FF id={id} text={text} {...(width ? { width } : {})} placeholder={t('form.date.ph')} />;
  }
  return (
    <label className="ff">
      <span>{text}</span>
      <input
        type="date"
        value={iso}
        className="datefield"
        style={width ? { width: width + 'px' } : undefined}
        title={fieldTitle(t, id, f.warn)}
        aria-label={name}
        readOnly={readOnly}
        onKeyDown={onFieldKeyDown}
        onChange={e => {
          if (readOnly) return;
          store.set(id, isoToDate(e.target.value));
          store.recompute();
        }}
      />
    </label>
  );
}

/** A form checkbox ('X' on the paper form). */
export function CheckField({ id, text }: { id: string; text: string }) {
  const store = usePlanStore();
  const readOnly = useReadOnly();
  const t = useT();
  const f = useField(id);
  return (
    <label className="tog2">
      <input
        type="checkbox"
        checked={f.value.trim() !== ''}
        disabled={readOnly}
        aria-label={label(id, t)}
        onKeyDown={onFieldKeyDown}
        onChange={e => store.setChecked(id, e.target.checked)}
      />
      <span>{text}</span>
    </label>
  );
}

export function Row({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={'row' + (className ? ' ' + className : '')}>{children}</div>;
}

/** N stacked full-width lines (Freigaben, Wetter-Beratung, FS-Beratung). */
export function Lines({ prefix, n, placeholder }: { prefix: string; n: number; placeholder?: string }) {
  const items = [];
  for (let i = 1; i <= n; i++) {
    items.push(
      <FieldInput key={i} id={prefix + i} {...(placeholder ? { placeholder: placeholder + ' ' + i } : {})} />,
    );
  }
  return <div className="lines">{items}</div>;
}
